import {
  type Locale,
  getLanguageFromLocale,
  getZoneFromSite,
  siteConfig,
} from '@seek/melways-sites';

import type { EmployerSiteNames, Environment } from 'src/siteConfig';

import {
  type Analytics,
  type AnalyticsEvent,
  createTealiumAdapter,
} from './createTealiumAdapter';
import { getBrandFromSiteForAnalytics } from './getBrandFromSiteForAnalytics';

interface AppStaticAttributes {
  siteSubsection: string;
}

interface DynamicAttributes {
  // app attributes
  site: EmployerSiteNames;
  locale: Locale;
  environment: Environment;

  // page attributes
  userId: number | null;
  seekAdvertiserId: string | null;
  extraBaseAttributes?: Record<string, unknown>;
}

export const createPageAnalyticsFn =
  <L extends AnalyticsEvent, V extends AnalyticsEvent>({
    siteSubsection,
  }: AppStaticAttributes): ((params: DynamicAttributes) => Analytics<L, V>) =>
  ({
    site,
    locale,
    environment,
    userId,
    seekAdvertiserId,
    extraBaseAttributes = {},
  }) => {
    const isDev = environment === 'dev';

    const brand = getBrandFromSiteForAnalytics({ site });

    return createTealiumAdapter({
      debug: isDev,
      baseAttributes: {
        // app static attributes
        siteSection: 'jobs',
        siteSubsection,

        // app dynamic attributes i.e. site and locale injected by Sku
        siteLanguage: getLanguageFromLocale(locale),
        siteCountry: siteConfig[site].classification.country,
        brand,
        zone: getZoneFromSite(site),

        // page dynamic base attributes
        isLoggedIn: true,
        userId,
        seekAdvertiserId,
        ...extraBaseAttributes,
      },
    });
  };
